import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { EGDSSpacing } from '@egds/react-core/spacing';
import { AuthenticationState, DeviceType } from '__generated__/api';
import { CustomerNotificationLocation } from '@shared-ui/customer-notifications';
import { Experiment, ExperimentControl, ExperimentVariant, useExperiment } from '@shared-ui/experiment-context';
import { ResultsMessagingModuleHeading } from '@shared-ui/lodging-property-listing';
import { SearchFormSignalsMap, useLodgingURLParamsToQueryInputs } from '@shared-ui/retail-search-tools-lodging';
import { useSignalPublisher } from '@shared-ui/signal-events-context';
import { getSharedUIListingsInputsWithSearchId, hasSearchCriteriaChanged } from 'stores/adapters';
import { OneKeyBannerToggle } from 'components/common/one-key-banner-toggle';
import { Pagination } from 'components/search/pagination/pagination';
import { ResultsMessagingModuleSharedUiWrapper } from 'components/search/results-messaging-module/results-messaging-module-shared-ui-wrapper';
import { CustomerNotifications } from '../../common/customer-notifications';
import { refreshAds } from '../pagination/pagination-utils';
import { PriceDisplayChangeOnboardingSharedUiWrapper } from '../price-display-change-onboarding/price-display-change-onboarding-wrapper';
import { PropertyListingWrapper } from '../property-listing/property-listing-wrapper';
/* eslint-disable complexity */
export const PropertySearchListings = inject('context', 'experiment', 'multiItem', 'uiPage', 'searchCriteria', 'searchResults', 'analyticsSearch', 'analytics', 'pageLayout')(observer(({
  context,
  experiment,
  multiItem,
  uiPage,
  searchCriteria,
  searchResults,
  analyticsSearch,
  analytics,
  pageLayout
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const listingsQueryInputs = useLodgingURLParamsToQueryInputs();
  const location = useLocation();
  if (!context || !searchCriteria || !searchResults || !analyticsSearch) {
    return null;
  }
  const usePropertyListingQuery = !(multiItem === null || multiItem === void 0 ? void 0 : multiItem.isPackageShoppingPath());
  const searchInputs = usePropertyListingQuery && listingsQueryInputs ? getSharedUIListingsInputsWithSearchId(listingsQueryInputs, context, analyticsSearch, location, pageLayout, searchCriteria, experiment) : undefined;
  const criteriaChanged = usePropertyListingQuery && hasSearchCriteriaChanged(context, searchCriteria, searchResults);
  const {
    exposure: lodgingSRPOneKey
  } = useExperiment('Loyalty_Banner_Test');
  const isLodgingSRPOneKeyInControl = Boolean(lodgingSRPOneKey.bucket === 0);
  const destinationRef = React.useRef(new URLSearchParams(location.search).get('destination'));
  const {
    publishSignal
  } = useSignalPublisher();
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const locationValue = searchParams.get('destination');
    if (locationValue && locationValue !== destinationRef.current) {
      destinationRef.current = locationValue;
      publishSignal({
        type: SearchFormSignalsMap.LOCATION_FIELD_UPDATE,
        payload: {
          locationValue
        }
      });
    }
  }, [location.search, publishSignal]);
  const handleNext = React.useCallback(() => {
    var _a, _b;
    if (((_b = (_a = searchResults === null || searchResults === void 0 ? void 0 : searchResults.pagination) === null || _a === void 0 ? void 0 : _a.trigger) === null || _b === void 0 ? void 0 : _b.__typename) === 'LodgingPaginationNavigationButtons') window.scrollTo(0, 0);
    refreshAds();
    if (searchResults && analytics && context && multiItem && uiPage && experiment && analyticsSearch) {
      searchResults.showNextPage(analytics, context, multiItem, uiPage, analyticsSearch, experiment, pageLayout);
    }
  }, [analytics, context, experiment, multiItem, searchResults, uiPage, analyticsSearch]);
  const handlePrev = React.useCallback(() => {
    var _a, _b;
    if (((_b = (_a = searchResults === null || searchResults === void 0 ? void 0 : searchResults.pagination) === null || _a === void 0 ? void 0 : _a.trigger) === null || _b === void 0 ? void 0 : _b.__typename) === 'LodgingPaginationNavigationButtons') window.scrollTo(0, 0);
    refreshAds();
    if (searchResults && analytics && context && multiItem && uiPage && experiment && analyticsSearch) {
      searchResults.showPreviousPage(analytics, context, multiItem, uiPage, analyticsSearch, experiment, pageLayout);
    }
  }, [analytics, context, experiment, multiItem, searchResults, uiPage, analyticsSearch]);
  const customSpacingProps = {
    margin: {
      block: 'two'
    },
    padding: {
      block: 'two'
    }
  };
  const authState = (_a = context === null || context === void 0 ? void 0 : context.user) === null || _a === void 0 ? void 0 : _a.authState;
  const isNotAnonymous = authState !== AuthenticationState.ANONYMOUS;
  const deviceType = (_b = context === null || context === void 0 ? void 0 : context.deviceInformation) === null || _b === void 0 ? void 0 : _b.type;
  const notMWeb = deviceType !== DeviceType.MOBILE_TABLET && deviceType !== DeviceType.MOBILE_PHONE;
  const inlineMadBannerTnLBucket = (_e = (_d = (_c = experiment === null || experiment === void 0 ? void 0 : experiment.exposures) === null || _c === void 0 ? void 0 : _c.BEX_mWeb_LSRP_inline_MAD_US) === null || _d === void 0 ? void 0 : _d.bucket) !== null && _e !== void 0 ? _e : 0;
  const showOneKeyBanner = inlineMadBannerTnLBucket === 0 || ((_f = context === null || context === void 0 ? void 0 : context.site) === null || _f === void 0 ? void 0 : _f.brand) !== 'expedia' || isNotAnonymous || notMWeb;
  const isMobileOrTab = deviceType === DeviceType.MOBILE_PHONE || deviceType === DeviceType.MOBILE_TABLET;
  const isPinned = Boolean(new URLSearchParams(location.search).get('selected'));
  const {
    exposure: isMetasearchBanners
  } = useExperiment('Web_banners_on_pinned_SRP');
  const hideBanner = !(multiItem === null || multiItem === void 0 ? void 0 : multiItem.isPackageShoppingPath()) && isPinned && isMobileOrTab && isMetasearchBanners.bucket === 1;
  return React.createElement("div", {
    "data-stid": "section-results"
  }, React.createElement(Experiment, {
    name: "BEX_mweb_PTP_inline_MAD_LSR_main_Gate"
  }, React.createElement(ExperimentControl, null), React.createElement(ExperimentVariant, {
    bucket: 1
  }, React.createElement(CustomerNotifications, {
    location: CustomerNotificationLocation.IN_PAGE,
    spacingProps: customSpacingProps
  }))), isLodgingSRPOneKeyInControl && showOneKeyBanner && !hideBanner && React.createElement(OneKeyBannerToggle, {
    analyticsSearch: analyticsSearch,
    experiment: experiment,
    analytics: analytics
  }), React.createElement(EGDSSpacing, {
    margin: {
      block: 'three'
    }
  }, React.createElement("div", null, usePropertyListingQuery ? React.createElement(ResultsMessagingModuleSharedUiWrapper, {
    searchInputs: searchInputs,
    criteriaChanged: criteriaChanged,
    searchResults: searchResults
  }) : React.createElement(ResultsMessagingModuleHeading, {
    text: (_h = (_g = searchResults.messagingModule) === null || _g === void 0 ? void 0 : _g.header) === null || _h === void 0 ? void 0 : _h.text
  }))), React.createElement(PriceDisplayChangeOnboardingSharedUiWrapper, null), React.createElement(PropertyListingWrapper, {
    searchInputs: searchInputs,
    criteriaChanged: criteriaChanged,
    handleNext: handleNext,
    handlePrev: handlePrev
  }), usePropertyListingQuery ? React.createElement(React.Fragment, null) : React.createElement(Pagination, {
    handleNext: handleNext,
    handlePrev: handlePrev
  }));
}));
PropertySearchListings.displayName = 'PropertySearchListings';